import { mapColorPalette, stateToRegionByName, WELL_ZOOM_LEVELS as ZOOM_LEVELS } from "../../../../../constants/Map";
import usStates from '../../../../../constants/Map/polygons/us-states.json';
import { getStateCountiesPolygon } from "../../../../../utils/mapHelper";
import { ICountyPublicWaterSystemResult, IRegionPublicWaterSystemResult, IWaterSystemGeoBlocks } from "../../../../../types";
import debounce from 'lodash/debounce';
import { handleMoveEnd } from "./handleMoveEnd";

type HandleZoomProps = {
  mapRef: any;
  prevZoom: number;
  prevSelectedCode: React.MutableRefObject<string | null | undefined>;
  prevSelectedCounty: React.MutableRefObject<string | null | undefined>;
  regionsHousingUnitsRef: React.RefObject<IRegionPublicWaterSystemResult[]>;
  countyHousingUnitsRef: React.RefObject<ICountyPublicWaterSystemResult[]>;
  geoBlocksGroupUnitsRef: React.RefObject<IWaterSystemGeoBlocks[]>;
};

export const handleZoom = async ({
  mapRef,
  prevZoom,
  prevSelectedCode,
  prevSelectedCounty,
  regionsHousingUnitsRef,
  countyHousingUnitsRef,
  geoBlocksGroupUnitsRef,
}: HandleZoomProps) => {
  const map = mapRef.current;
  if (!map) return;

  const zoom = map.getZoom();
  
  const mapInstance = map as mapboxgl.Map & { _isZoomProcessing?: boolean };
  
  if (mapInstance._isZoomProcessing) return;
  mapInstance._isZoomProcessing = true;

  try {
    if (zoom < ZOOM_LEVELS.STATE.MIN) {

      await handleRegionZoom(map, regionsHousingUnitsRef);
    }

    if (isCountyZoomRange(zoom, prevZoom)) {

      await handleCountyZoom(map, prevSelectedCode, countyHousingUnitsRef);
    }


    prevZoom = zoom;
  } finally {
    setTimeout(() => {
      mapInstance._isZoomProcessing = false;
    }, 100);
  }
};

const handleRegionZoom = async (
  map: mapboxgl.Map, 
  regionsViolationsRef: React.RefObject<IRegionPublicWaterSystemResult[]>
) => {
  const source = map.getSource('maine');
  if (!source) return;
  
  (source as mapboxgl.GeoJSONSource).setData(usStates as GeoJSON.FeatureCollection);

  if (!regionsViolationsRef?.current?.length) {
    console.warn('regionsViolationsRef.current is empty or undefined');
    return;
  }

  const regionToColorMapping = createRegionColorMapping(regionsViolationsRef.current);
  updateRegionColors(map, regionToColorMapping);
};

const handleCountyZoom = async (
  map: mapboxgl.Map,
  prevSelectedCode: React.MutableRefObject<string | null | undefined>,
  countyViolationsRef: React.RefObject<ICountyPublicWaterSystemResult[]>
) => {
  const countiesGeo = await getStateCountiesPolygon(prevSelectedCode?.current || '');
  if (!countiesGeo) return;

  const source = map.getSource('maine');
  if (!source) return;
  
  (source as mapboxgl.GeoJSONSource).setData(countiesGeo);

  const countyColorMapping = createCountyColorMapping(countyViolationsRef?.current);
  if (countyColorMapping) {
    map.setPaintProperty('maine', 'fill-color', [
      'match',
      ['get', 'name'],
      ...Object.entries(countyColorMapping).flat(),
      '#fff',
    ]);
  }
};

const isCountyZoomRange = (zoom: number, prevZoom: number): boolean => {
  return (
    zoom < ZOOM_LEVELS.COUNTY.MAX &&
    zoom > ZOOM_LEVELS.COUNTY.MIN &&
    (prevZoom >= ZOOM_LEVELS.COUNTY.MAX || prevZoom <= ZOOM_LEVELS.COUNTY.MIN)
  );
};

const createRegionColorMapping = (regions: IRegionPublicWaterSystemResult[]): Record<string, string> => {
  return regions.reduce((acc, region, index) => {
    acc[region?.epaRegion] = mapColorPalette[index];
    return acc;
  }, {} as Record<string, string>);
};

const createCountyColorMapping = (counties: ICountyPublicWaterSystemResult[] | undefined | null): Record<string, string> | null => {
  if (!counties) return null;
  
  return counties.reduce((acc, county, index) => {
    const color = mapColorPalette[index] || '#fff';
    acc[county?.county_name] = color;
    return acc;
  }, {} as Record<string, string>);
};

const updateRegionColors = (map: mapboxgl.Map, regionToColorMapping: Record<string, string>) => {
  if (!stateToRegionByName || typeof stateToRegionByName !== 'object') {
    console.warn('stateToRegionByName is invalid:', stateToRegionByName);
    return;
  }

  map.setPaintProperty('maine', 'fill-color', [
    'match',
    ['get', 'name'],
    ...Object.entries(stateToRegionByName)
      .map(([stateName, { epaRegion }]) => {
        const color = regionToColorMapping[epaRegion] || '#ffffff';
        return [stateName, color];
      })
      .flat(),
    '#ffffff',
  ]);
};

export const debouncedHandleMoveEndWell = debounce(handleMoveEnd, 250);
